// src/pages/MarketingView.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tab,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useQuery } from 'react-query';

import client from '../../../../../API';

import ActiveUsersTable from '../adminComponents/userInfo';
import SharedWithTable  from './sharedwith';
import CRMTable         from './crmInfo';

import UpdateDialog   from './sendUpdate';
import EmailCRMDialog from './sendEmailCRM';
import AddCRMDialog   from './addcrmdialog';

const StatCard = ({ label, value, loading }) => (
  <Card>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {loading ? <CircularProgress size={32} /> : <Typography variant="h3">{value}</Typography>}
    </CardContent>
  </Card>
);

export default function MarketingView() {
  const [tab, setTab] = useState('active');

  /* selection states */
  const [selectedActive, setSelectedActive]   = useState([]);
  const [selectedShared, setSelectedShared]   = useState([]);
  const [selectedCRM,    setSelectedCRM]      = useState([]);

  /* dialog states */
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openEmail,  setOpenEmail]  = useState(false);
  const [openAdd,    setOpenAdd]    = useState(false);

  /* counts */
  const { data: users = [],  isLoading: usersLoading  } = useQuery('users',  client.users.userList);
  const { data: shared = [], isLoading: sharedLoading } = useQuery('shared', client.users.fetchSharedWith);

  const counts = useMemo(() => {
    const emails    = new Set(users.map((u) => u.email));
    const pending   = shared.filter((c) => !emails.has(c.email));
    return {
      totalUsers:      users.length,
      totalSharedWith: shared.length,
      pendingUsers:    pending.length,
    };
  }, [users, shared]);

  /* helpers */
  const currentSelected =
    tab === 'active' ? selectedActive : tab === 'shared' ? selectedShared : selectedCRM;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* stat cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <StatCard label="Total Users" value={counts.totalUsers} loading={usersLoading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard label="Total Shared With" value={counts.totalSharedWith} loading={sharedLoading} />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard label="Pending Users" value={counts.pendingUsers} loading={usersLoading || sharedLoading} />
        </Grid>
      </Grid>

      {/* tables */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <TabContext value={tab}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              Marketing Dashboard
            </Typography>

            {tab === 'active' && (
              <Button variant="contained" onClick={() => setOpenUpdate(true)} disabled={!selectedActive.length}>
                Send Update
              </Button>
            )}
            {tab === 'shared' && (
              <Button variant="contained" onClick={() => setOpenEmail(true)} disabled={!selectedShared.length}>
                Send Promotional
              </Button>
            )}
            {tab === 'crm' && (
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={() => setOpenEmail(true)} disabled={!selectedCRM.length}>
                  Send Email
                </Button>
                <Button variant="outlined" onClick={() => setOpenAdd(true)}>
                  Add to CRM
                </Button>
              </Stack>
            )}
          </Stack>

          <TabList onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
            <Tab label="Active Users" value="active" />
            <Tab label="Shared With"  value="shared" />
            <Tab label="CRM"          value="crm" />
          </TabList>

          <TabPanel value="active" sx={{ p: 0, pt: 3 }}>
            <ActiveUsersTable
              onSelectionChange={setSelectedActive}
              rowsData={users}
              loading={usersLoading}
            />
          </TabPanel>

          <TabPanel value="shared" sx={{ p: 0, pt: 3 }}>
            <SharedWithTable onSelectionChange={setSelectedShared} />
          </TabPanel>

          <TabPanel value="crm" sx={{ p: 0, pt: 3 }}>
            <CRMTable onSelectionChange={setSelectedCRM} />
          </TabPanel>
        </TabContext>
      </Paper>

      {/* dialogs */}
      <UpdateDialog
        open={openUpdate}
        onClose={() => setOpenUpdate(false)}
        userData={selectedActive}
      />
      <EmailCRMDialog
        open={openEmail}
        onClose={() => setOpenEmail(false)}
        userData={currentSelected}
      />
      <AddCRMDialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
      />
    </Box>
  );
}
